import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@core/utils/cn';
import { Icon } from './Icon';

/**
 * Replaces `mat-icon-button`.
 *
 * 32x32 with 4px padding and an 18px glyph — the effective values from
 * styles.scss:1686-1697, which forced `--mdc-icon-button-state-layer-size: 32px` over
 * Material's default 40px. The header notification bell is the one documented
 * exception and passes `size="lg"`.
 *
 * `label` is REQUIRED: an icon-only button has no accessible name otherwise, and the
 * Angular templates relied on `matTooltip` for this, which does not provide one.
 *
 * ============================================================================
 * A BARE STRING CHILD IS WRAPPED IN `<Icon>`
 * ============================================================================
 *
 * Both call styles occur across the port — `<IconButton><Icon>edit</Icon></IconButton>` and
 * `<IconButton>edit</IconButton>` — and only the first used to work. A raw string rendered as the
 * literal text "edit" instead of a glyph, because nothing gave it the `material-icons` class, and
 * because the button is a fixed 32x32 box the overflowing words then overlapped their neighbours.
 * Manage Roles and Manage Users both shipped unreadable action columns this way.
 *
 * Wrapping here rather than correcting each call site fixes every present and future one. It is
 * unambiguous: this control is icon-only by contract — that is exactly why `label` is required — so a
 * string child can only ever be a ligature name.
 */
const iconButton = cva(
  'inline-flex flex-none items-center justify-center rounded-sm bg-transparent p-1 ' +
    'transition-colors ' +
    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent ' +
    'disabled:cursor-not-allowed disabled:opacity-40',
  {
    variants: {
      size: {
        sm: 'h-7 w-7 [&>.material-icons]:text-[16px]',
        /** 32px — the app-wide default */
        md: 'h-8 w-8 [&>.material-icons]:text-[18px]',
        lg: 'h-10 w-10 [&>.material-icons]:text-[22px]',
      },
      tone: {
        default: 'text-fg-3 hover:bg-surface-2 hover:text-fg',
        accent: 'text-accent hover:bg-accent-soft',
        danger: 'text-danger hover:bg-danger-soft',
        success: 'text-success hover:bg-success-soft',
        warning: 'text-warning hover:bg-warning-soft',
        inverse: 'text-white/90 hover:bg-white/15 hover:text-white',
      },
    },
    defaultVariants: { size: 'md', tone: 'default' },
  },
);

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>,
    VariantProps<typeof iconButton> {
  /** Accessible name. Required — icon-only controls have no text content. */
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, size, tone, label, type = 'button', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      className={cn(iconButton({ size, tone }), className)}
      {...rest}
    >
      {/*
        The size variants above target `[&>.material-icons]`, so the glyph has to be a DIRECT child
        for the per-size font size to apply. `<Icon>` supplies that class; a bare string does not.
      */}
      {typeof children === 'string' ? <Icon>{children}</Icon> : children}
    </button>
  );
});
